import React, { useState, useEffect } from 'react';
import { Save, Calendar } from 'lucide-react';

export default function CalendarConfig({ config, loading, error, canManage, onSave }) {
  const [formData, setFormData] = useState({
    nombre: 'Configuración Principal',
    descripcion: '',
    trabaja_lunes: true,
    trabaja_martes: true,
    trabaja_miercoles: true,
    trabaja_jueves: true,
    trabaja_viernes: true,
    trabaja_sabado: false,
    trabaja_domingo: false,
    es_24_7: false
  });

  useEffect(() => {
    if (config) {
      setFormData(config);
    }
  }, [config]);

  const handleChange = (e) => {
    const { name, type, checked, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    await onSave(formData);
  };

  const diasSemana = [
    { key: 'trabaja_lunes', label: 'Lunes' },
    { key: 'trabaja_martes', label: 'Martes' },
    { key: 'trabaja_miercoles', label: 'Miércoles' },
    { key: 'trabaja_jueves', label: 'Jueves' },
    { key: 'trabaja_viernes', label: 'Viernes' },
    { key: 'trabaja_sabado', label: 'Sábado' },
    { key: 'trabaja_domingo', label: 'Domingo' }
  ];

  if (!canManage) {
    return (
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 text-center">
        <p className="text-yellow-700">No tienes permisos para modificar la configuración del calendario.</p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-6 border border-blue-200">
        <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
          <Calendar className="w-5 h-5" />
          Configuración de Días Laborables
        </h3>
        <p className="text-sm text-gray-600 mb-6">
          Define qué días de la semana son considerados laborables para la empresa.
        </p>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {diasSemana.map(dia => (
            <label
              key={dia.key}
              className={`flex items-center gap-3 p-4 rounded-lg border-2 cursor-pointer transition-all ${
                formData[dia.key]
                  ? 'bg-blue-100 border-blue-500 text-blue-700'
                  : 'bg-white border-gray-200 text-gray-600'
              }`}
            >
              <input
                type="checkbox"
                name={dia.key}
                checked={formData[dia.key]}
                onChange={handleChange}
                className="w-5 h-5 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
              />
              <span className="font-medium">{dia.label}</span>
            </label>
          ))}
        </div>
      </div>

      <div className="bg-white rounded-xl p-6 border border-gray-200">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">Información General</h3>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Nombre de la Configuración
            </label>
            <input
              type="text"
              name="nombre"
              value={formData.nombre}
              onChange={handleChange}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              placeholder="Ej: Configuración Principal"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Descripción
            </label>
            <textarea
              name="descripcion"
              value={formData.descripcion}
              onChange={handleChange}
              rows="3"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              placeholder="Descripción opcional de la configuración..."
            />
          </div>

          <div>
            <label className="flex items-center gap-3 p-4 bg-purple-50 border border-purple-200 rounded-lg cursor-pointer">
              <input
                type="checkbox"
                name="es_24_7"
                checked={formData.es_24_7}
                onChange={handleChange}
                className="w-5 h-5 text-purple-600 rounded focus:ring-2 focus:ring-purple-500"
              />
              <div>
                <span className="font-medium text-gray-800">Operación 24/7</span>
                <p className="text-sm text-gray-600">La empresa opera las 24 horas, los 7 días de la semana</p>
              </div>
            </label>
          </div>
        </div>
      </div>

      <div className="bg-gray-50 rounded-xl p-6 border border-gray-200">
        <h4 className="font-semibold text-gray-800 mb-3">Resumen</h4>
        <div className="space-y-2 text-sm text-gray-700">
          <p>
            <span className="font-medium">Días laborables:</span>{' '}
            {diasSemana.filter(d => formData[d.key]).map(d => d.label).join(', ') || 'Ninguno'}
          </p>
          <p>
            <span className="font-medium">Total de días por semana:</span>{' '}
            {diasSemana.filter(d => formData[d.key]).length} días
          </p>
          {formData.es_24_7 && (
            <p className="text-purple-600 font-medium">✓ Operación continua 24/7</p>
          )}
        </div>
      </div>

      <div className="flex justify-end">
        <button
          type="submit"
          disabled={loading}
          className="flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
        >
          <Save className="w-5 h-5" />
          Guardar Configuración
        </button>
      </div>
    </form>
  );
}
